import React, { useState, useCallback, useContext, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "./Profile.css";
import { MyContext } from "../../../../Context/MyContext";
import API from "../../../../Api/api";
import { clearTokens } from "../../../../Api/authToken";
import { stopPresence } from "../../../../Services/presenceService";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LanguageSelector from "../../../../Components/LanguageSelector";
import AppIcon from "../../../../Components/AppIcon";
import OtpModal from "../auth/OtpModal";
import Cropper from "react-easy-crop";
import {
  FaInstagram, FaFacebookF, FaLinkedinIn,
  FaXTwitter, FaYoutube, FaWhatsapp,
} from "react-icons/fa6";

const socialIcons = {
  FaInstagram:  <FaInstagram/>,  FaFacebookF: <FaFacebookF/>,
  FaLinkedinIn: <FaLinkedinIn/>, FaXTwitter:  <FaXTwitter/>,
  FaYoutube:    <FaYoutube/>,    FaWhatsapp:  <FaWhatsapp/>,
};

// ── Image crop helper ────────────────────────────────────────
const createCroppedImage = (imageSrc, croppedAreaPixels) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => {
      const canvas = document.createElement("canvas");
      const ctx    = canvas.getContext("2d");
      canvas.width  = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;
      ctx.drawImage(image,
        croppedAreaPixels.x, croppedAreaPixels.y,
        croppedAreaPixels.width, croppedAreaPixels.height,
        0, 0, croppedAreaPixels.width, croppedAreaPixels.height);
      canvas.toBlob(blob => {
        if (!blob) { reject(new Error("Canvas empty")); return; }
        resolve(URL.createObjectURL(blob));
      }, "image/jpeg", 0.92);
    });
    image.addEventListener("error", reject);
    image.src = imageSrc;
  });

// ── Crop Modal ───────────────────────────────────────────────
const CropModal = ({ imageSrc, onSave, onCancel }) => {
  const [crop, setCrop]                     = useState({ x:0, y:0 });
  const [zoom, setZoom]                     = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const onCropComplete = useCallback((_, px) => setCroppedAreaPixels(px), []);
  const handleSave = async () => {
    try { onSave(await createCroppedImage(imageSrc, croppedAreaPixels)); }
    catch { toast.error("Could not crop image."); }
  };
  return ReactDOM.createPortal(
    <div className="crop-portal-overlay">
      <div className="crop-portal-box">
        <p className="crop-portal-title">Crop your photo</p>
        <div className="crop-portal-area">
          <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={1}
            onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete}/>
        </div>
        <input className="crop-zoom-slider" type="range" min={1} max={3} step={0.05}
          value={zoom} onChange={e => setZoom(Number(e.target.value))}/>
        <div className="crop-portal-actions">
          <button className="crop-save-btn" onClick={handleSave}>Save Photo</button>
          <button className="crop-cancel-btn" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Icons ────────────────────────────────────────────────────
const Icons = {
  Camera:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="28" height="28"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>,
  Key:      () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="15" height="15"><circle cx="7.5" cy="15.5" r="5.5"/><path d="M21 2l-9.6 9.6M15.5 7.5l3 3"/></svg>,
  Shield:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="15" height="15"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  Bookmark: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="15" height="15"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>,
  LogOut:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="15" height="15"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  MapPin:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="13" height="13"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  Check:    () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="10" height="10"><polyline points="20 6 9 17 4 12"/></svg>,
  Heart: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="15" height="15">
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
    </svg>
  ),
};

// ── City Selector (State → City two-step dropdown) ───────────
const CitySelectorField = ({ selectedCity, onCitySelect, states, cities }) => {
  const [open, setOpen]               = useState(false);
  const [step, setStep]               = useState('state');      // 'state' | 'city'
  const [selectedState, setSelectedState] = useState(null);
  const [search, setSearch]           = useState('');
  const [results, setResults]         = useState(states || []);
  const ddRef                         = useRef(null);
  const debounceRef                   = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ddRef.current && !ddRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openDropdown = () => {
    setStep('state');
    setSelectedState(null);
    setSearch('');
    setResults(states || []);
    setOpen(true);
  };

  const handleStateSelect = (state) => {
    setSelectedState(state);
    setSearch('');
    setResults((cities || []).filter(c => c.state_id === state.id));
    setStep('city');
  };

  const handleCitySelect = (city) => {
    onCitySelect(city, selectedState);
    setOpen(false);
    setStep('state');
    setSelectedState(null);
    setSearch('');
  };

  const handleBack = () => {
    setStep('state');
    setSelectedState(null);
    setSearch('');
    setResults(states || []);
  };

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!val.trim()) {
        setResults(
          step === 'state'
            ? (states || [])
            : (cities || []).filter(c => c.state_id === selectedState?.id)
        );
        return;
      }
      if (step === 'state') {
        setResults((states || []).filter(s =>
          s.state_name?.toLowerCase().includes(val.toLowerCase())
        ));
      } else {
        setResults(
          (cities || [])
            .filter(c => c.state_id === selectedState?.id)
            .filter(c => c.city_name?.toLowerCase().includes(val.toLowerCase()))
        );
      }
    }, 250);
  };

  return (
    <div className="profile-city-selector" ref={ddRef}>
      {/* Trigger button */}
      <button
        type="button"
        className="profile-city-trigger"
        onClick={() => open ? setOpen(false) : openDropdown()}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          width="13" height="13" style={{ flexShrink: 0 }}>
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
        <span className="profile-city-trigger-label">
          {selectedCity ? selectedCity.city_name : 'Let Us Know Your Preference'}
        </span>
        {selectedCity && (
          <span
            className="profile-city-clear"
            onClick={e => { e.stopPropagation(); handleCitySelect(null); }}
            title="Clear"
          ><AppIcon name="close" size={12} /></span>
        )}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          width="11" height="11"
          style={{ marginLeft: 'auto', transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="profile-city-dropdown">
          {/* Step header */}
          <div className="profile-city-dd-header">
            {step === 'city' && (
              <button type="button" className="profile-city-back-btn" onClick={handleBack}>
                ← {selectedState?.state_name}
              </button>
            )}
            {step === 'state' && (
              <span className="profile-city-dd-title">Select State</span>
            )}
          </div>

          {/* Search */}
          <div className="profile-city-search">
            <input
              type="text"
              placeholder={step === 'state' ? 'Search state…' : 'Search city…'}
              value={search}
              onChange={e => handleSearch(e.target.value)}
              autoFocus
            />
          </div>

          {/* List */}
          <div className="profile-city-list">
            {/* "All Cities" only in city step */}
            {step === 'city' && (
              <button
                type="button"
                className={`profile-city-item ${!selectedCity ? 'active' : ''}`}
                onClick={() => handleCitySelect(null)}
              >
                All Cities
              </button>
            )}

            {results.length === 0 && (
              <div className="profile-city-empty">
                No {step === 'state' ? 'states' : 'cities'} found
              </div>
            )}

            {step === 'state'
              ? results.map(s => (
                  <button
                    type="button"
                    key={s.id}
                    className="profile-city-item profile-city-item--state"
                    onClick={() => handleStateSelect(s)}
                  >
                    {s.state_name}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                      width="11" height="11" style={{ marginLeft: 'auto' }}>
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </button>
                ))
              : results.map(c => (
                  <button
                    type="button"
                    key={c.id}
                    className={`profile-city-item ${selectedCity?.id === c.id ? 'active' : ''}`}
                    onClick={() => handleCitySelect(c)}
                  >
                    {c.city_name}
                  </button>
                ))
            }
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main ProfileSidebar ──────────────────────────────────────
const ProfileSidebar = ({ formData, setFormData, verified, setVerified, setActiveView }) => {
  const { user, setUser, logininput, imgUrl, states, cities, selectedCity, changeCity, localizationEnabled } = useContext(MyContext);
  const navigate = useNavigate();

  const safeSignup  = logininput?.signup || [];
  const phoneConfig = safeSignup.find(f => f.field === "phone");
  const emailConfig = safeSignup.find(f => f.field === "email");

  const [socials,          setSocials]         = useState(user?.social || []);
  const [editingSocialId,  setEditingSocialId]  = useState(null);
  const [newLink,          setNewLink]          = useState("");
  const [editField,        setEditField]        = useState(null);
  const [otpModal,         setOtpModal]         = useState(false);
  const [otpType,          setOtpType]          = useState("");
  const [otpPin,           setOtpPin]           = useState("");
  const [rawImageSrc,      setRawImageSrc]      = useState(null);
  const [showCropModal,    setShowCropModal]    = useState(false);
  const [saving,           setSaving]           = useState(false);
  const [photoUploading,   setPhotoUploading]   = useState(false);

  // ✅ Profile photo display — resolve from backend path
  const [profilePhoto, setProfilePhoto] = useState(() => {
    const p = user?.profile;
    if (!p) return null;
    if (p.startsWith('http') || p.startsWith('https') || p.startsWith('blob:') || p.startsWith('data:')) return p;
    return imgUrl ? imgUrl(p) : p;
  });

  // Sync when user updates
  useEffect(() => {
    const p = user?.profile;
    if (!p) { setProfilePhoto(null); return; }
    if (p.startsWith('http') || p.startsWith('https') || p.startsWith('blob:') || p.startsWith('data:')) {
      setProfilePhoto(p);
    } else {
      setProfilePhoto(imgUrl ? imgUrl(p) : p);
    }
  }, [user?.profile]); // eslint-disable-line

  // ── Save field to backend ────────────────────────────────
  const saveFieldToBackend = async (field, value) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("access_token");
      const res   = await API.put('/auth/profile', { [field]: value }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updated = res.data;
      setUser(updated);
      localStorage.setItem("loggedUser", JSON.stringify(updated));
      toast.success("Profile updated!");
    } catch (err) {
      toast.error(err.response?.data?.error || "Update failed");
    } finally { setSaving(false); }
  };

  // ── Save multiple fields at once ─────────────────────────
  const saveMultipleFieldsToBackend = async (payload) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("access_token");
      const res   = await API.put('/auth/profile', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updated = res.data;
      setUser(updated);
      localStorage.setItem("loggedUser", JSON.stringify(updated));
      toast.success("Location updated!");
    } catch (err) {
      toast.error(err.response?.data?.error || "Update failed");
    } finally { setSaving(false); }
  };

  const handleSave = async () => {
    const fieldMap = {
      name:     { key: "user_name",  value: formData.name     },
      email:    { key: "email",      value: formData.email    },
      phone:    { key: "user_phone", value: formData.phone    },
    };
    const target = fieldMap[editField];
    if (target) {
      if (editField === "email") setVerified(p => ({ ...p, email: false }));
      if (editField === "phone") setVerified(p => ({ ...p, phone: false }));
      await saveFieldToBackend(target.key, target.value);
    }
    setEditField(null);
  };

  // ── City selection handler (syncs with HeroSection via context + backend) ──
  const handleCitySelect = async (city, state) => {
    // 1. Update context so HeroSection also reflects it
    changeCity(city);

    // 2. Update local formData display
    setFormData(prev => ({ ...prev, location: city ? city.city_name : '' }));

    // 3. Persist to backend — same fields as HeroSection uses
    const stateId = city?.state_id ?? state?.id ?? null;
    const token = localStorage.getItem("user_access_token") || localStorage.getItem("access_token");
    if (token) {
      try {
        const res = await API.put('/auth/profile', {
          selected_city_id:   city?.id || null,
          selected_state_id:  stateId,
          // also write human-readable location so admin's preferred_location
          // column is always populated with the city name
          location:           city ? city.city_name : '',
          preferred_location: city ? city.city_name : '',
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data) {
          setUser(res.data);
          localStorage.setItem("loggedUser", JSON.stringify(res.data));
        }
      } catch (err) {
        toast.error("Failed to update city preference");
      }
    }
  };

  // ── OTP ──────────────────────────────────────────────────
  const openOtpModal = async (type) => {
    if (type === "email" && !formData.email) { toast.error("Enter email first!"); return; }
    if (type === "phone" && !formData.phone) { toast.error("Enter phone first!"); return; }
    if (type === "email") {
      try {
        const res = await API.post('/auth/send-otp', { email: formData.email, purpose: 'signup' });
        if (res.data.otp_dev) { setOtpPin(res.data.otp_dev); toast.info(`Dev OTP: ${res.data.otp_dev}`); }
        else { setOtpPin(""); toast.success(`OTP sent to ${formData.email}`); }
      } catch (err) { toast.error(err.response?.data?.error || "Failed"); return; }
    } else {
      setOtpPin("123456");
      toast.success("Demo OTP: 123456");
    }
    setOtpType(type); setOtpModal(true);
  };

  // ── Profile image upload ─────────────────────────────────
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    const reader = new FileReader();
    reader.onload = () => { setRawImageSrc(reader.result); setShowCropModal(true); };
    reader.readAsDataURL(file);
  };

  const handleCropSave = async (blobUrl) => {
    setProfilePhoto(blobUrl);
    setRawImageSrc(null);
    setShowCropModal(false);
    setPhotoUploading(true);

    try {
      const blob = await fetch(blobUrl).then(r => r.blob());
      const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
      const form = new FormData();
      form.append('file', file);

      const upRes = await API.post('/upload/profile', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const uploadedPath = upRes.data.path;
      await saveFieldToBackend('profile', uploadedPath);
      setProfilePhoto(imgUrl ? imgUrl(uploadedPath) : uploadedPath);
      toast.success("Profile photo updated!");
    } catch (err) {
      console.error('Photo upload error:', err);
      toast.error("Photo upload failed. Please try again.");
      setProfilePhoto(null);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    setProfilePhoto(null);
    await saveFieldToBackend('profile', '');
    toast.info("Profile photo removed");
  };

  // ── Sign out ─────────────────────────────────────────────
  const handleSignOut = () => {
    stopPresence();
    clearTokens('user');
    localStorage.removeItem('active_status_token');
    setUser(null);
    toast.success("Signed out successfully!");
    navigate("/login");
  };

  // ── Social helpers ───────────────────────────────────────
  const visibleSocials = socials.filter(i => i.is_show);
  const handleEditSocial  = (item) => { setEditingSocialId(item.id); setNewLink(item.link || ""); };
  const handleSaveSocial  = async (id) => {
    const updated = socials.map(i => i.id === id ? { ...i, link: newLink } : i);
    setSocials(updated); setEditingSocialId(null);
    await saveFieldToBackend('social', updated);
  };
  const handleDeleteSocial = async (id) => {
    const updated = socials.map(i => i.id === id ? { ...i, is_show: false } : i);
    setSocials(updated);
    await saveFieldToBackend('social', updated);
    toast.info("Social removed!");
  };

  if (!user) return (
    <div className="profile-sidebar">
      <h3>Please login</h3>
      <button onClick={() => navigate("/login")}>Go to Login</button>
    </div>
  );

  // Derive what the current city display should be:
  // prefer the context's selectedCity (shared with HeroSection), fallback to user data
  const displayCity = selectedCity || (user?.selected_city_id ? null : null);

  return (
    <>
      {showCropModal && rawImageSrc && (
        <CropModal imageSrc={rawImageSrc} onSave={handleCropSave}
          onCancel={() => { setRawImageSrc(null); setShowCropModal(false); }}/>
      )}

      <div className="profile-sidebar">
        {/* Profile Image */}
        <div className="profile-img-box">
          <div className="profile-img-wrapper">
            {photoUploading && (
              <div className="photo-uploading-overlay">
                <span className="photo-spinner"/>
              </div>
            )}
            {profilePhoto ? (
              <>
                <img src={profilePhoto} alt="profile" onError={() => setProfilePhoto(null)}/>
                <label className="img-overlay-label" title="Change photo">
                  <input type="file" hidden accept="image/*" onChange={handleImageChange}/>
                  <Icons.Camera/>
                </label>
              </>
            ) : (
              <label className="camera-center" title="Upload photo">
                <input type="file" hidden accept="image/*" onChange={handleImageChange}/>
                <Icons.Camera/>
                <span className="camera-label">Add Photo</span>
              </label>
            )}
          </div>
          {profilePhoto && !photoUploading && (
            <div className="profile-img-actions">
              <label className="img-action-btn img-edit-btn">Edit
                <input type="file" hidden accept="image/*" onChange={handleImageChange}/>
              </label>
              <button className="img-action-btn img-delete-btn" onClick={handleRemovePhoto}>Remove</button>
            </div>
          )}
          <div className="profile-bio">
            <strong className="profile-bio-name">{formData.name || user.user_name}</strong>
            <span className="profile-bio-email">{formData.email || user.email}</span>
            {(selectedCity || formData.location || user.location) && (
              <span className="profile-bio-location">
                <Icons.MapPin/>
                {selectedCity ? selectedCity.city_name : (formData.location || user.location)}
              </span>
            )}
          </div>
        </div>

        {/* Fields */}
        <div className="profile-fields-wrap">
          {/* Name */}
          <div className="profile-field">
            <label>Full Name</label>
            {editField === "name" ? (
              <div className="field-edit-wrap">
                <input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} autoFocus placeholder="Full name"/>
                <div className="field-btn-row">
                  <button className="save-btn" onClick={handleSave} disabled={saving}>{saving?"Saving…":"Save"}</button>
                  <button className="field-cancel-btn" onClick={() => setEditField(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="field-display-row">
                <span className="field-value">{formData.name || user.user_name}</span>
                <button className="field-edit-btn" onClick={() => setEditField("name")}>Edit</button>
              </div>
            )}
          </div>

          {/* Email */}
          <div className="profile-field">
            <label>Email Address</label>
            {editField === "email" ? (
              <div className="field-edit-wrap">
                <input value={formData.email} type="email" onChange={e => setFormData({...formData, email: e.target.value})} autoFocus/>
                <div className="field-btn-row">
                  <button className="save-btn" onClick={handleSave} disabled={saving}>{saving?"Saving…":"Save"}</button>
                  <button className="field-cancel-btn" onClick={() => setEditField(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="field-display-row">
                <span className="field-value">{formData.email || user.email}</span>
                <div className="field-right">
                  {verified.email
                    ? <span className="badge-verified"><Icons.Check/> Verified</span>
                    : emailConfig?.otp_show && <button className="badge-unverified" onClick={() => openOtpModal("email")}>Verify</button>
                  }
                  <button className="field-edit-btn" onClick={() => setEditField("email")}>Edit</button>
                </div>
              </div>
            )}
          </div>

          {/* Phone */}
          <div className="profile-field">
            <label>Phone Number</label>
            {editField === "phone" ? (
              <div className="field-edit-wrap">
                <input value={formData.phone} type="tel" onChange={e => setFormData({...formData, phone: e.target.value})} autoFocus/>
                <div className="field-btn-row">
                  <button className="save-btn" onClick={handleSave} disabled={saving}>{saving?"Saving…":"Save"}</button>
                  <button className="field-cancel-btn" onClick={() => setEditField(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="field-display-row">
                <span className="field-value">{formData.phone || user.user_phone}</span>
                <div className="field-right">
                  {verified.phone
                    ? <span className="badge-verified"><Icons.Check/> Verified</span>
                    : phoneConfig?.otp_show && <button className="badge-unverified" onClick={() => openOtpModal("phone")}>Verify</button>
                  }
                  <button className="field-edit-btn" onClick={() => setEditField("phone")}>Edit</button>
                </div>
              </div>
            )}
          </div>

          {/* ── Location — State → City Selector ── */}
          <div className="profile-field">
            <label>
              Preferred Location
              <span className="profile-field-badge">
                {/* <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                City-based */}
              </span>
            </label>
            <CitySelectorField
              selectedCity={selectedCity}
              onCitySelect={handleCitySelect}
              states={states}
              cities={cities}
            />
            {selectedCity && (
              <p className="profile-city-hint">
                {/* Admins can view your preferred city to provide relevant listings. */}
              </p>
            )}
          </div>

          {localizationEnabled !== false && (
            <div className="profile-field profile-field--language">
              <label>Language & Region</label>
              <LanguageSelector variant="inline" className="profile-lang-select" />
            </div>
          )}

          {/* Socials */}
          {visibleSocials.length > 0 && (
            <div className="profile-field">
              <label>Social Links</label>
              <div className="profile-socials">
                {visibleSocials.map(item => (
                  <div key={item.id || item.social_id} className="social-item">
                    {editingSocialId === (item.id || item.social_id) ? (
                      <>
                        <input value={newLink} onChange={e => setNewLink(e.target.value)} placeholder="Enter link"/>
                        <div className="field-btn-row">
                          <button className="save-btn" onClick={() => handleSaveSocial(item.id || item.social_id)}>Save</button>
                          <button className="field-cancel-btn" onClick={() => setEditingSocialId(null)}>Cancel</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <a href={item.link} target="_blank" rel="noopener noreferrer" className="social-icon">
                          {socialIcons[item.icon] || item.icon}
                        </a>
                        <div className="social-actions">
                          <button onClick={() => handleEditSocial(item)}>Edit</button>
                          <button onClick={() => handleDeleteSocial(item.id || item.social_id)}>Delete</button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="profile-actions">
          <button onClick={() => setActiveView("reset")}><Icons.Key/> Reset Password</button>
          <button onClick={() => setActiveView("kyc")}><Icons.Shield/> KYC Verification</button>
          <button onClick={() => setActiveView("bookings")}><Icons.Bookmark/> My Bookings</button>
          <button onClick={() => setActiveView("wishlist")}><Icons.Heart /> My Wishlist</button>
          <button className="logout-btn" onClick={handleSignOut}><Icons.LogOut/> Sign Out</button>
        </div>
      </div>

      <OtpModal
        otpModal={otpModal} setOtpModal={setOtpModal}
        otpType={otpType} otpPin={otpPin}
        setVerified={setVerified}
        email={formData.email}
        onResend={() => openOtpModal(otpType)}
      />
    </>
  );
};

export default ProfileSidebar;